import {
  EnrichmentResult,
  GetApplicationStateResponse,
  GetLanguagesResponse,
  GetProfileResponse,
  GetSkillsResponse,
  GetSpecializationsResponse,
  ParseCVResponse,
  UpdateAvatarRequest,
  UpdateLanguagesRequest,
  UpdateProfileRequest,
  UpdateSkillsRequest,
  UpdateSpecializationsRequest,
} from '@internal/types/api';

import { httpClient, mutationEndpoint, queryEndpoint } from '../system';

const getProfile = queryEndpoint({
  entity: 'profile',
  queryKey: () => ['profile'],
  queryFn: (_, nextContext?) =>
    httpClient.auto<GetProfileResponse>({
      url: '/api/me',
      method: 'GET',
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

const updateProfile = mutationEndpoint({
  actionName: 'updateProfile',
  mutationKey: ['update-profile'],
  mutationFn: ({
    apiKey,
    ...payload
  }: UpdateProfileRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: queryClient => {
    queryClient.invalidateQueries({ queryKey: ['profile'] });
  },
});

const updateAvailability = mutationEndpoint({
  actionName: 'updateAvailability',
  mutationKey: ['update-availability'],
  mutationFn: ({
    apiKey,
    ...payload
  }: UpdateProfileRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile/availability',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: queryClient => {
    queryClient.invalidateQueries({ queryKey: ['profile'] });
  },
});

const getApplicationState = queryEndpoint({
  entity: 'applicationState',
  queryKey: () => ['application-state'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<GetApplicationStateResponse>({
      url: '/api/profile/application-state',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

const getSkills = queryEndpoint({
  entity: 'skills',
  queryKey: () => ['skills'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<GetSkillsResponse>({
      url: '/api/profile/skills',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: [],
});

const updateSkills = mutationEndpoint({
  actionName: 'updateSkills',
  mutationKey: ['update-skills'],
  mutationFn: ({
    apiKey,
    ...payload
  }: UpdateSkillsRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile/skills',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: queryClient => {
    queryClient.invalidateQueries({ queryKey: ['skills'] });
  },
});

const getSpecializations = queryEndpoint({
  entity: 'specializations',
  queryKey: () => ['specializations'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile/specializations',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: [],
});

const updateSpecializations = mutationEndpoint({
  actionName: 'updateSpecializations',
  mutationKey: ['update-specializations'],
  mutationFn: ({
    apiKey,
    ...payload
  }: UpdateSpecializationsRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile/specializations',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: queryClient => {
    queryClient.invalidateQueries({ queryKey: ['specializations'] });
    queryClient.invalidateQueries({ queryKey: ['profile'] });
  },
});

const getLanguages = queryEndpoint({
  entity: 'languages',
  queryKey: () => ['languages'],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<GetLanguagesResponse>({
      url: '/api/profile/languages',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: [],
});

const updateLanguages = mutationEndpoint({
  actionName: 'updateLanguages',
  mutationKey: ['update-languages'],
  mutationFn: ({
    apiKey,
    ...payload
  }: UpdateLanguagesRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: '/api/profile/languages',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: queryClient => {
    queryClient.invalidateQueries({ queryKey: ['languages'] });
  },
});

const updateAvatar = mutationEndpoint({
  actionName: 'updateAvatar',
  mutationKey: ['update-avatar'],
  mutationFn: ({ apiKey, photo }: UpdateAvatarRequest & { apiKey: string }) =>
    httpClient.auto<GetSpecializationsResponse>({
      url: `/upload/${apiKey as string}/photo`,
      method: 'POST',
      data: photo,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
});

const parseCV = mutationEndpoint({
  actionName: 'parseCV',
  mutationKey: ['parse-cv'],
  mutationFn: ({
    apiKey,
    attachment,
  }: {
    apiKey: string;
    attachment: FormData;
  }) =>
    httpClient.auto<ParseCVResponse>({
      url: '/api/cv-parser',
      method: 'POST',
      data: attachment,
      headers: {
        'auth-key': apiKey,
        'Content-Type': 'multipart/form-data',
      },
    }),
});

const triggerEnrichment = mutationEndpoint({
  actionName: 'triggerEnrichment',
  mutationKey: ['trigger-enrichment'],
  mutationFn: ({
    apiKey,
    attachmentId,
    linkedinUrl,
  }: {
    apiKey: string;
    attachmentId?: string;
    linkedinUrl?: string;
  }) =>
    httpClient.auto<EnrichmentResult>({
      url: '/api/enrichment/trigger',
      method: 'POST',
      data: { attachmentId, linkedinUrl },
      headers: {
        'auth-key': apiKey,
      },
    }),
});

const getEnrichmentJob = queryEndpoint({
  entity: 'enrichmentJob',
  queryKey: (params?: { apiKey: string; jobId: string }) => [
    'enrichment-job',
    params?.jobId ?? '',
  ],
  queryFn: (params?: { apiKey: string; jobId: string }, nextContext?) =>
    httpClient.auto<EnrichmentResult>({
      url: `/api/enrichment/jobs/${params?.jobId}`,
      method: 'GET',
      headers: {
        'auth-key': params?.apiKey,
      },
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

export {
  getApplicationState,
  getEnrichmentJob,
  getLanguages,
  getProfile,
  getSkills,
  getSpecializations,
  parseCV,
  triggerEnrichment,
  updateAvailability,
  updateAvatar,
  updateLanguages,
  updateProfile,
  updateSkills,
  updateSpecializations,
};
